import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { isAccountPath } from '../../features/account/paths';
import { Shell, TextLink } from '../SharedComponents';
import { track } from '../telemetry';
import { BRAND } from '../theme/paletteDark';
import { FONTS } from '../theme/typography';
import { createAccountClient, type GetAccessToken } from './client';
import { AccountDataContext, StorageSourceContext, type AccountData, type StorageSource } from './context';
import { readSnapshot, snapshotHasData } from './importer';
import { ACCOUNT_HINT_KEY, IMPORT_RECORD_KEY } from './keys';
import { AccountSession, type SessionView, type SyncReport } from './session';
import { deviceStorage, safeGet, safeSet } from './storage';

/**
 * Holds the signed-in user's account data for everything under it
 * (CONTRACT §6–7). Mounted inside `AuthKitProvider` and `BrowserRouter`, above
 * `<Routes>`, so every page — the shared schedule included — sees the same
 * plans.
 *
 * Logged out, or before the sign-up questions are done, it stays out of the
 * way: the hooks read and write `localStorage` as they always have. Once the
 * account can be shown, the device's plans are moved into it, and from then on
 * the hooks read the account's copy and every change goes to the server.
 *
 * While the account is loading on a browser that has already shown it, the
 * page waits rather than drawing an empty schedule; if it can't load, the page
 * says so and offers Retry. `/account/*` is never held back, so the Account
 * page and the questions always work.
 */

const DEVICE_VIEW: SessionView = { mode: 'device', ready: true, notice: null };

/** A notice that waited longer than this for the app to show it is dropped. */
const NOTICE_TTL_MS = 2 * 60_000;

function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Plain http on a LAN host has no randomUUID; the ids must still be UUIDs.
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function report(r: SyncReport): void {
  track({ t: 'account_sync', ...r });
  if (import.meta.env.DEV) console.info('[account]', r);
}

interface State {
  view: SessionView;
  epoch: number;
  session: AccountSession | null;
}

export default function AccountDataProvider({ children }: { children: ReactNode }) {
  const { isLoading, user, getAccessToken, signOut } = useAuth();
  const userId = user?.id ?? null;
  const { pathname } = useLocation();

  /** Read once: which account this browser showed last time, if any. */
  const [hint] = useState(() => safeGet(deviceStorage, ACCOUNT_HINT_KEY));
  const [state, setState] = useState<State>({ view: DEVICE_VIEW, epoch: 0, session: null });

  const tokenRef = useRef<GetAccessToken>(getAccessToken);
  useEffect(() => {
    tokenRef.current = getAccessToken;
  }, [getAccessToken]);

  const sessionRef = useRef<AccountSession | null>(null);
  const modeRef = useRef(state.view.mode);
  useEffect(() => {
    modeRef.current = state.view.mode;
  }, [state.view.mode]);

  // One session per signed-in user.
  useEffect(() => {
    if (isLoading || !userId) return;
    const session: AccountSession = new AccountSession({
      workosUserId: userId,
      client: createAccountClient({ getAccessToken: (options) => tokenRef.current(options) }),
      flushClient: createAccountClient({ getAccessToken: (options) => tokenRef.current(options), retries: 0 }),
      storage: deviceStorage,
      hinted: hint === userId,
      newId: uuid,
      report,
      locks: typeof navigator !== 'undefined' ? navigator.locks : undefined,
      onChange: (view, adopt) =>
        setState((s) => ({
          view,
          session,
          // Into or out of the account, or a newer copy of it: the hooks read again.
          epoch: adopt || (s.view.mode === 'account') !== (view.mode === 'account') ? s.epoch + 1 : s.epoch,
        })),
    });
    sessionRef.current = session;
    session.start();
    return () => {
      session.dispose();
      sessionRef.current = null;
      setState((s) => ({ view: DEVICE_VIEW, session: null, epoch: s.view.mode === 'account' ? s.epoch + 1 : s.epoch }));
    };
  }, [isLoading, userId, hint]);

  // Signed out: the next visit shouldn't wait for an account.
  useEffect(() => {
    if (!isLoading && !userId) safeSet(deviceStorage, ACCOUNT_HINT_KEY, null);
  }, [isLoading, userId]);

  // The contract's two signals from the account pages, and the browser's.
  useEffect(() => {
    const onboardingComplete = () => sessionRef.current?.onboardingComplete();
    const beforeSignOut = (e: Event) => {
      const detail = (e as CustomEvent<{ waitUntil?: (p: Promise<boolean>) => void } | undefined>).detail;
      detail?.waitUntil?.(sessionRef.current ? sessionRef.current.flushNow() : Promise.resolve(true));
    };
    const online = () => sessionRef.current?.online();
    const storage = (e: StorageEvent) => {
      // Another tab finished the move, while this one still shows the device's
      // copy: writing that copy back would put old lists on the device. Start
      // again from the account instead. Never mid-questions.
      if (
        e.key === IMPORT_RECORD_KEY &&
        e.newValue === null &&
        modeRef.current === 'device' &&
        !isAccountPath(window.location.pathname)
      ) {
        window.location.reload();
        return;
      }
      sessionRef.current?.storageEvent(e.key, e.newValue);
    };
    window.addEventListener('mcw:onboarding-complete', onboardingComplete);
    window.addEventListener('mcw:before-sign-out', beforeSignOut);
    window.addEventListener('online', online);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener('mcw:onboarding-complete', onboardingComplete);
      window.removeEventListener('mcw:before-sign-out', beforeSignOut);
      window.removeEventListener('online', online);
      window.removeEventListener('storage', storage);
    };
  }, []);

  const session = state.session && !isLoading && state.session.userId === userId ? state.session : null;
  const view = session ? state.view : DEVICE_VIEW;

  // Before the session's first report: the same rule it starts from.
  let ready = view.ready;
  if (!session) {
    if (isLoading) ready = hint === null;
    else if (userId) ready = hint !== userId && snapshotHasData(readSnapshot(deviceStorage));
  }

  const isAccount = view.mode === 'account' && session !== null;
  const storageSource = useMemo<StorageSource>(
    () => ({
      epoch: state.epoch,
      create: () => (isAccount && session ? session.createStorage() : { ...deviceStorage }),
    }),
    [state.epoch, isAccount, session],
  );

  const dismissNotice = useCallback(() => sessionRef.current?.dismissNotice(), []);
  const retry = useCallback(() => sessionRef.current?.retry(), []);
  const flush = useCallback(() => sessionRef.current?.flushNow() ?? Promise.resolve(true), []);

  const notice = view.notice && Date.now() - view.notice.at < NOTICE_TTL_MS ? view.notice : null;
  const data = useMemo<AccountData>(
    () => ({ mode: view.mode, notice, dismissNotice, retry, flush }),
    [view.mode, notice, dismissNotice, retry, flush],
  );

  let content: ReactNode = children;
  if (!ready && !isAccountPath(pathname)) {
    content =
      view.mode === 'error' ? (
        <AccountUnavailable onRetry={retry} onSignOut={() => signOut({ returnTo: 'https://myclimateweek.com/' })} />
      ) : (
        <Shell sx={{ display: 'grid', placeItems: 'center' }}>
          <CircularProgress sx={{ color: BRAND.parchment }} />
        </Shell>
      );
  }

  return (
    <AccountDataContext.Provider value={data}>
      <StorageSourceContext.Provider value={storageSource}>{content}</StorageSourceContext.Provider>
    </AccountDataContext.Provider>
  );
}

/** Signed in, and the account didn't answer. Never an empty schedule in its place. */
function AccountUnavailable({ onRetry, onSignOut }: { onRetry: () => void; onSignOut: () => void }) {
  return (
    <Shell>
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 3, textAlign: 'center' }}>
        <Box>
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, fontWeight: 700, mb: 1 }}>
            We couldn't load your schedule
          </Typography>
          <Typography sx={{ opacity: 0.7, fontSize: 14, mb: 2 }}>
            Your account didn't respond. Your plans are safe. Check your connection and try again.
          </Typography>
          <Button
            onClick={onRetry}
            sx={{ background: BRAND.parchment, color: BRAND.mocha, borderRadius: 999, px: 3, fontWeight: 700 }}
          >
            Retry
          </Button>
          <Box sx={{ mt: 2 }}>
            <TextLink onClick={onSignOut}>Log out</TextLink>
          </Box>
        </Box>
      </Box>
    </Shell>
  );
}
